import { uniq } from 'lodash';
import type ApplicationModel from './ApplicationModel';
import type Query from './Query';
import {
  type ModelName,
  type RecordId,
  type RecordRef,
} from 'javascripts/shared-types';
import { QueryBatcher } from './QueryBatcher';

type Batch = {
  query: Query<any, any>;
  recordIds: RecordRef[];
  queuedCallbacks: Function[];
};

const BATCH_LIMIT = 50;

/**
 * The BatchedRecordLoader can optimize loading multiple records of different types
 * with a single GraphQL request. All records of the same type must use the same
 * query. The first query added to the batch will be used to load all other records
 * of the same type.
 *
 * Records that are not visible/not found will simply resolve to undefined.
 */
class BatchedRecordLoader {
  batcher = new QueryBatcher();
  batches: Map<ModelName, Batch> = new Map();
  batchTimeout: ReturnType<typeof setTimeout> | null = null;

  // Results
  recordsById: Map<RecordId, ApplicationModel> = new Map();
  recordsByReferenceNum: Map<string, ApplicationModel> = new Map();

  /**
   * Adds a record reference (ID or ref num) to the batch for the given query (model type).
   */
  addToBatch(query: Query<any, any>, recordId: RecordRef) {
    const model = query.model.typename as ModelName;
    let batch = this.batches.get(model);

    if (!batch) {
      batch = {
        query: query.per(BATCH_LIMIT),
        recordIds: [],
        queuedCallbacks: [],
      };
    }

    batch.recordIds = batch.recordIds.concat(recordId);

    this.batches.set(model, batch);

    if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => this.runBatch(), 0);
    }

    if (batch.recordIds.length >= BATCH_LIMIT) {
      // Run batch immediately, this query batch is at the limit
      this.runBatch();
    }

    return batch;
  }

  /**
   * Load a single record with a given query. If this is the first record of its type,
   * then the query provided will be used for all records that are loaded for the current batch.
   * This method returns a promise that will resolve after all records in the current
   * event loop are added to the batch.
   */
  loadRecord(query: Query<any, any>, id: RecordRef): Promise<ApplicationModel> {
    const record =
      this.getRecordById(id as RecordId) || this.getRecordByReferenceNum(id);
    if (record) {
      return Promise.resolve(record);
    }

    const batch = this.addToBatch(query, id);

    return new Promise(resolve => {
      batch.queuedCallbacks.push(() => {
        const record =
          this.getRecordById(id as RecordId) ||
          this.getRecordByReferenceNum(id);

        resolve(record!);
      });
    });
  }

  async run() {
    const { batches } = this;
    this.batches = new Map();

    await this.batcher.execute(batcher => {
      void Array.from(batches.values()).map(
        async ({ query, recordIds, queuedCallbacks }) => {
          const records = await batcher.all(
            query.where({ id: uniq(recordIds) })
          );
          records.forEach(record => {
            this.recordsById.set(record.id, record);

            if (
              'referenceNum' in record &&
              typeof record.referenceNum === 'string' &&
              record.referenceNum
            ) {
              this.recordsByReferenceNum.set(record.referenceNum, record);
            }

            // projects have a referencePrefix field which works like referenceNum on other records
            else if (
              'referencePrefix' in record &&
              typeof record.referencePrefix === 'string' &&
              record.referencePrefix
            ) {
              this.recordsByReferenceNum.set(record.referencePrefix, record);
            }
          });
          queuedCallbacks.forEach(callback => {
            callback();
          });
        }
      );
    });
  }

  runBatch() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    void this.run();
  }

  getRecordById(recordId: RecordId) {
    return this.recordsById.get(recordId);
  }

  getRecordByReferenceNum(referenceNum: string) {
    return this.recordsByReferenceNum.get(referenceNum);
  }

  resetCache() {
    this.recordsById = new Map();
    this.recordsByReferenceNum = new Map();
  }
}

export default BatchedRecordLoader;
